<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Optimiser Sheet" avatarImage="/glass/img/optimiser-overview-sheet.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Optimiser Sheet</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelSheetStatusBadge :status="sheet.status" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Size">
              {{sheet.height}} x {{sheet.width}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Stock">
              <LinkStock :id="sheet.stockId" :name="sheet.stock.name" />
            </OverviewBoxListItem>

            <OverviewBoxListItem title="Updated">{{sheet.updatedAt | formatDateTime | formatHyphenEmptyValue}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Completed">{{sheet.dateClosed | formatDateTime | formatHyphenEmptyValue}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>
      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-sheet-summary">
              <i class="far fa-clipboard-list-check"></i> Sheet Details
            </Tab>
            <Tab tabid="tab-sheet-layout">
              <i class="far fa-th"></i> Sheet Layout
            </Tab>
            <Tab tabid="tab-sheet-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-sheet-summary">

              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="downloadExportData" icon="fa-file-export" v-if="sheetHasExportData">Download Export Data</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Size">{{sheet.height}} x {{sheet.width}}</DataItem>
                  <DataItem title="Stock"><LinkStock :id="sheet.glassLevelId" :name="sheet.stock.name" /></DataItem>
                  <DataItem title="Trim">{{trimInfo}}</DataItem>
                  <DataItem title="Updated">{{sheet.updatedAt | formatDateTime | formatHyphenEmptyValue}}</DataItem>
                </template>
              </DataPageArea>
              
              <div class="row mt-4">
                <div class="col-12">
                  <ContentTitle>Sheet Layout</ContentTitle>
                  <div class="optimiser-sheet-preview-container">
                    <div v-html="sheet.svgDisplayData" class="optimiser-sheet-preview" />
                  </div>
                </div>
              </div>
              <div class="row mt-4">
                <div class="col-12">
                  <ContentTitle>Allocations</ContentTitle>
                  <div class="optimiser-sheet-allocation-list">
                    <OptimiserAllocationList :allocationList="allocationList" />
                  </div>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-sheet-layout">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Sheet Layout</ContentTitle>
                  <OptimiserAllocLayoutList :allocationList="allocationList" />
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-sheet-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.optimiserSheet" :linkId="sheet.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<style>
/* Note: Not scoped to allow nested SVG styling */
.optimiser-sheet-preview-container {
  display: relative;
  width: 100%;
  height: 75vh;
  background: #efefef;
}
.optimiser-sheet-allocation-list {
  
}
.optimiser-sheet-preview {
  display: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 4px;
}
.optimiser-sheet-preview > svg {
  width: 100%;
  height: 100%;
  max-height: 70vh;
  max-width: 80vw;
}
</style>

<script>
import { saveAs } from 'file-saver'

import LabelSheetStatusBadge from '@/modules/optimiser/components/labelSheetStatusBadge'
import OptimiserAllocationList from '@/modules/optimiser/components/optimiserAllocationList'
import OptimiserAllocLayoutList from '@/modules/optimiser/components/optimiserAllocLayoutList'

import { LinkTypes, OptimiserSheetStatusTypes } from '@/store/types'

export default {
  name: 'OptimiserSheet',
  components: {
    LabelSheetStatusBadge,
    OptimiserAllocationList,
    OptimiserAllocLayoutList
  },
  data() {
    return {
      sheet: {
        stock: {},
        glassLevel: {},
        optimiserSheetAllocations: []
      }
    }
  },
  computed: {
    activityStreamURL() {
      if(!this.$route.params.id)
        return null
      return `/optimiser/sheets/${this.$route.params.id}/activitystream`
    },
    sheetTitle() {
      if(this.pageDataLoading || !this.sheet)
        return 'Sheet'
        
      return `${this.sheet.stock.name} - ${this.sheet.height}x${this.sheet.width}`
    },
    allocationList() {
      if(this.pageDataLoading || !this.sheet)
        return []

      return this.sheet.optimiserSheetAllocations || []
    },
    trimInfo() {
      if(this.sheet) {
        return `T:${this.sheet.trimTop}mm, R:${this.sheet.trimRight}mm, B:${this.sheet.trimBottom}mm, L:${this.sheet.trimLeft}mm`
      }
      return ''
    },
    sheetHasExportData() {
      if(this.sheet) {
        if(this.sheet.status == OptimiserSheetStatusTypes.completed || this.sheet.status == OptimiserSheetStatusTypes.cutting) {
          if(this.sheet.cuttingExportData && this.sheet.cuttingExportFilename)
            return true
        }
      }
      return false
    }
  },
  mounted() {
    this.requestData()
  },
  created() {
    this.LinkTypes = LinkTypes
  },
  methods: {
    requestData() {
      this.pageLoadData(this.$apiService.optimiserService.getOptimiserSheet(this.$route.params.id))
      .then(response => {
        this.sheet = response.data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    downloadExportData() {
      // Download
      var exportDataBlob = new Blob([this.sheet.cuttingExportData], {
        type: 'application/octet-stream'
      });
      
      var filename = this.sheet.cuttingExportFilename
      saveAs(exportDataBlob, filename)
    }
  }
}
</script>